<template>
  <FlashNotifications ref="flash" />

  <div class="min-h-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- Compact object page header: icon/title row + slim facet tabs, in our design tokens -->
    <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6">

      <div class="flex items-center justify-between gap-3 h-14">
        <div class="flex items-center gap-2.5 min-w-0">
          <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" :d="orgIcon.path"/>
            </svg>
          </div>
          <div class="min-w-0">
            <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none truncate">Organization Structure</h1>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none truncate">{{ summaryLabel }}</p>
          </div>
        </div>
        <button type="button" @click="openAdd('company', null)"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm flex-shrink-0">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
          </svg>
          <span class="hidden sm:inline">Add Company</span>
          <span class="sm:hidden">Add</span>
        </button>
      </div>

      <!-- Facet tabs -->
      <div class="flex items-center gap-5">
        <button type="button" @click="viewMode = 'table'"
          :class="viewMode === 'table' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
          class="px-0.5 pb-2.5 text-sm font-semibold border-b-2 transition-colors">
          Table
        </button>
        <button type="button" @click="viewMode = 'chart'"
          :class="viewMode === 'chart' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
          class="px-0.5 pb-2.5 text-sm font-semibold border-b-2 transition-colors">
          Org Chart
        </button>
      </div>
    </div>

    <!-- Page content -->
    <div class="py-6">

      <!-- Info panel: explains the hierarchy, dismissible -->
      <div v-if="showInfo" class="mx-4 sm:mx-6 mb-4 flex items-start gap-3 rounded-xl border border-blue-100 dark:border-blue-900/40 bg-blue-50/60 dark:bg-blue-900/10 px-4 py-3.5">
        <div class="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
          </svg>
        </div>
        <div class="flex-1 min-w-0 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          <p>
            <span class="font-semibold text-gray-900 dark:text-white">Companies</span> represent the individual business entities within <span class="font-semibold text-gray-900 dark:text-white">{{ org.name }}</span>, such as subsidiaries, brands, or regional offices. Each company keeps its own set of departments.
          </p>
          <p class="mt-1">
            <span class="font-semibold text-gray-900 dark:text-white">Departments</span> sit inside a company to organize its teams, such as Engineering, Sales, or Finance. They don't need to match across companies.
          </p>
        </div>
        <button type="button" @click="dismissInfo" title="Dismiss"
          class="w-7 h-7 flex items-center justify-center rounded-lg text-blue-400 dark:text-blue-500 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-100/70 dark:hover:bg-blue-900/30 transition-colors flex-shrink-0">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Loading skeleton -->
      <div v-if="store.loading && !store.fetchedAt" class="mx-4 sm:mx-6 bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-pulse divide-y divide-gray-100 dark:divide-gray-700">
        <div v-for="i in 4" :key="i" class="flex items-center gap-3 px-4 sm:px-6 py-3.5 sm:py-4">
          <div class="w-7 h-7 rounded-lg bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
          <div class="h-3 w-40 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="store.error" class="mx-4 sm:mx-6 bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center py-24 sm:py-32 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.error }}</p>
        <button @click="retry" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-sm transition-colors">
          Try again
        </button>
      </div>

      <!-- Empty state -->
      <div v-else-if="store.companies.length === 0" class="mx-4 sm:mx-6 bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center py-20 sm:py-24 text-center px-4">
        <div class="w-20 h-20 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-6">
          <svg class="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"/>
          </svg>
        </div>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">No companies yet</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400 max-w-xs mb-8">Add your first company to start building your organization structure.</p>
        <button @click="openAdd('company', null)" class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm">
          Add Company
        </button>
      </div>

      <!-- Tree table -->
      <div v-else-if="viewMode === 'table'" class="mx-4 sm:mx-6 bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <TransitionGroup name="tree-row" tag="div" class="divide-y divide-gray-50 dark:divide-gray-800">
          <TreeRow v-for="company in store.companies" :key="company.id"
            :node="company" type="company" :depth="0"
            @action="handleAction" />
        </TransitionGroup>
      </div>

      <!-- Org chart -->
      <div v-else class="px-4 sm:px-6">
        <OrgChartTreegraph :companies="store.companies" :org-name="org.name" />
      </div>
    </div>
  </div>

  <!-- Add/Edit modal -->
  <AddNodeModal v-if="modal.open"
    :title="modal.title"
    :type="modal.type"
    :is-editing="!!modal.editing"
    :submit-label="modal.editing ? 'Save' : 'Add'"
    :saving="modal.saving"
    :error="modal.error"
    :initial-name="modal.editing?.name || ''"
    :initial-description="modal.editing?.description || ''"
    @close="closeModal"
    @submit="submitModal" />
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { pageTitle } from 'utils/pageTitle.js'
import { orgStore } from 'controllers/vue_components/organizations/store'
import { useCompanyStructureStore } from 'stores/companyStructure.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import TreeRow from 'controllers/vue_components/organizations/structure/TreeRow.vue'
import AddNodeModal from 'controllers/vue_components/organizations/structure/AddNodeModal.vue'
import OrgChartTreegraph from 'controllers/vue_components/organizations/structure/OrgChartTreegraph.vue'
import { NODE_ICONS } from 'controllers/vue_components/organizations/structure/nodeIcons.js'

const flash = ref(null)
const orgGlobalStore = orgStore()
const store = useCompanyStructureStore()

const org = computed(() => orgGlobalStore.current_org || {})
const viewMode = ref('table')
const orgIcon = NODE_ICONS.org

// The router sets a generic title on navigation; once the org loads, refine it with the org name.
watch(() => org.value.name, (name) => {
  if (name) document.title = pageTitle('Structure', name)
}, { immediate: true })

const INFO_DISMISSED_KEY = 'org_structure_info_dismissed'
const showInfo = ref(localStorage.getItem(INFO_DISMISSED_KEY) !== 'true')

function dismissInfo() {
  showInfo.value = false
  localStorage.setItem(INFO_DISMISSED_KEY, 'true')
}

const summaryLabel = computed(() => {
  const companyCount = store.companies.length
  const departmentCount = store.companies.reduce((sum, c) => sum + (c.departments?.length || 0), 0)
  return `${companyCount} ${companyCount === 1 ? 'company' : 'companies'} · ${departmentCount} ${departmentCount === 1 ? 'department' : 'departments'}`
})

const modal = ref({ open: false, type: null, parent: null, editing: null, title: '', saving: false, error: '' })

onMounted(async () => {
  await store.fetchIfStale(org.value.id)
})

async function retry() {
  store.invalidate()
  await store.fetchIfStale(org.value.id)
}

function openAdd(type, parent) {
  modal.value = {
    open: true, type, parent, editing: null,
    title: type === 'company' ? 'Add Company' : 'Add Department',
    saving: false, error: '',
  }
}

function openEdit(type, node, parent) {
  modal.value = {
    open: true, type, parent, editing: node,
    title: type === 'company' ? 'Edit Company' : 'Edit Department',
    saving: false, error: '',
  }
}

function closeModal() {
  modal.value.open = false
}

// The API's `message` field is an internal identifier (e.g. "company_created"), not
// display text, so success/error copy shown to the user is built here instead.
function typeLabel(type) {
  return type === 'company' ? 'Company' : 'Department'
}

function actionErrorMessage(res) {
  const validationErrors = res.error?.data?.data
  if (Array.isArray(validationErrors) && validationErrors.length) return validationErrors.join(', ')
  return 'Something went wrong. Please try again.'
}

async function submitModal(payload) {
  modal.value.saving = true
  modal.value.error = ''

  const { type, parent, editing } = modal.value
  const { departments: prefillDepartmentNames, ...corePayload } = payload
  let res

  if (type === 'company') {
    res = editing ? await store.updateCompany(editing.id, corePayload) : await store.addCompany(corePayload)

    if (!editing && !res.js_error && res.response.success && prefillDepartmentNames?.length) {
      const companyId = res.response.data.id
      await Promise.all(prefillDepartmentNames.map((deptName) => store.addDepartment(companyId, { name: deptName })))
    }
  } else {
    res = editing
      ? await store.updateDepartment(parent.id, editing.id, corePayload)
      : await store.addDepartment(parent.id, corePayload)
  }

  modal.value.saving = false

  if (!res.js_error && res.response.success) {
    closeModal()
    flash.value?.show(`${typeLabel(type)} ${editing ? 'updated' : 'created'} successfully.`, 'notice')
  } else {
    modal.value.error = actionErrorMessage(res)
  }
}

async function handleAction({ action, type, node, parent }) {
  if (action === 'add') {
    openAdd(type, parent)
  } else if (action === 'edit') {
    openEdit(type, node, parent)
  } else if (action === 'delete') {
    const res = type === 'company'
      ? await store.deleteCompany(node.id)
      : await store.deleteDepartment(parent.id, node.id)

    if (!res.js_error && res.response.success) {
      flash.value?.show(`${typeLabel(type)} deleted successfully.`, 'notice')
    } else {
      flash.value?.show(actionErrorMessage(res), 'alert')
    }
  }
}
</script>

<style scoped>
.tree-row-enter-active,
.tree-row-leave-active {
  transition: all 0.25s ease;
}
.tree-row-enter-from,
.tree-row-leave-to {
  opacity: 0;
  transform: translateX(-8px);
}
.tree-row-leave-active {
  position: absolute;
}
</style>
